import Button from "components/base/Button";
import { Input } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { BsArrowDownCircle } from "react-icons/bs";
import { FaBarcode, FaRegCopy } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import http from "utils/http";

const BarcodePage = () => {
  const [searchParams, setSearchParams] = useSearchParams({ code: "" });
  const code = searchParams.get("code");
  const [loading, setLoading] = useState(false);
  const [barcodeSvg, setBarcodeSvg] = useState("");

  // ['code128', 'code39', 'ean', 'ean13', 'ean14', 'ean8', 'gs1', 'gs1_128', 'gtin', 'isbn', 'isbn10', 'isbn13', 'issn', 'itf', 'jan', 'pzn', 'upc', 'upca']

  let barcodeType = "unknown";
  if (code.length === 12) {
    barcodeType = "upc";
  } else if (code.length === 13) {
    barcodeType = "ean13";
  } else if (code.length === 14) {
    barcodeType = "itf";
  }

  async function generate() {
    if (barcodeType === "unknown")
      return Alert("error", "Invalid barcode number.");

    setLoading(true);
    try {
      const res = await http.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}download/barcode/${barcodeType}/${code}?svg=1`,
        {
          responseType: "blob",
          withCredentials: true,
        },
      );
      const fileContent = await res.data.text();
      setBarcodeSvg(fileContent);
    } catch (e) {
      const errorMessage = await e.response.data.text();
      Alert("error", errorMessage);
    }

    setLoading(false);
  }

  const copybarcodeAsPng = () => {
    if (!barcodeSvg) {
      Alert("error", "No barcode to copy.");
      return;
    }

    // Convert SVG to PNG
    const svgBlob = new Blob([barcodeSvg], { type: "image/svg+xml" });
    const img = new Image();
    img.src = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const scaleFactor = 4;
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scaleFactor;
      canvas.height = img.height * scaleFactor;
      const ctx = canvas.getContext("2d"); // context is used to draw the image on the canvas
      ctx.scale(scaleFactor, scaleFactor); // scale the image to 2x for better quality
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        navigator.clipboard
          .write([new ClipboardItem({ "image/png": blob })])
          .then(() => {
            Alert("success", "Barcode copied to clipboard.");
          })
          .catch((err) => {
            Alert("error", "Failed to copy barcode.");
            console.error("Failed to copy barcode: ", err);
          });
      }, "image/png");
    };
  };

  async function download() {
    if (barcodeType === "unknown")
      return Alert("error", "Invalid barcode number.");

    setLoading(true);
    try {
      const res = await http.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}download/barcode/${barcodeType}/${code}`,
        {
          responseType: "blob",
          withCredentials: true,
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${code}_${barcodeType}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      const errorMessage = await e.response.data.text();
      Alert("error", errorMessage);
    }

    setLoading(false);
  }

  return (
    <div
      className="bg-no-repeat bg-cover bg-center h-screen flex flex-col overflow-auto"
      style={{
        backgroundImage:
          "url(https://cdn.waboba.com/assets/promo_images/IMG_6450.JPG)",
      }}
    >
      <div className=" z-0 absolute inset-0 dark:bg-gray-900 dark:bg-opacity-60"></div>
      <div className="p-10 flex-1 flex justify-center items-center">
        <div className="pt-10 pb-2 px-16 w-full max-w-xl rounded-3xl bg-white dark:bg-gray-700 bg-opacity-80 dark:bg-opacity-90 backdrop-blur">
          <div>
            <div className="flex justify-center">
              <img
                src={"https://cdn.waboba.com/public/waboba-logo-w-waboba.png"}
                alt="Waboba"
                width={150}
              />
            </div>

            <h2 className="text-center mt-6">Barcode Generator</h2>
            <div className="mt-8">
              <Input
                className="w-full text-center py-1 tracking-wider text-lg"
                style={{ height: "auto" }}
                placeholder="Input Barcode Number 输入条码数字"
                value={code}
                onChange={(e) =>
                  setSearchParams({ code: e.target.value.replace(/ /g, "") })
                }
              />
            </div>

            <div className="text-center mt-2">
              Type:{" "}
              <span className="font-semibold uppercase">{barcodeType}</span>
            </div>

            {barcodeSvg && (
              <div className="mt-8 flex justify-center h-fit">
                <div
                  dangerouslySetInnerHTML={{
                    __html: barcodeSvg.replace(
                      /<svg([^>]*)>/,
                      (match, svgAttributes) => {
                        return `<svg${svgAttributes} transform="scale(2)">`;
                      },
                    ),
                  }}
                />
              </div>
            )}
          </div>

          <div className="mt-6 text-sm">
            <div>使用说明:</div>
            <ul className="list-decimal list-inside">
              <li>确保输入正确的数字后，再生成条码。</li>
              <li>
                输入12位数字系统会默认生成UPC格式的条码，确保用于美洲客户订单时使用UPC格式条码。
              </li>
              <li>
                输入13位数字系统会默认生成EAN格式的条码，确保用于欧洲、亚洲、澳洲以及中东市场客户订单时使用EAN格式条码。
              </li>
              <li>
                输入14位数字系统会默认生成ITF格式的条码，确保用于外箱是使用ITF格式条码。
              </li>
              <li>
                请务必检查生成出的条码格式和数位是否正确，如因使用人疏忽检查导致条码出错需要承担部分的责任。
              </li>
            </ul>
          </div>

          <div className="mt-6 flex justify-center space-x-6">
            <Button
              loading={loading}
              disabled={loading || !code}
              onClick={generate}
              icon={<FaBarcode className="mr-2" />}
            >
              生成条码
            </Button>
            <Button
              loading={loading}
              disabled={loading || !code}
              onClick={copybarcodeAsPng}
              icon={<FaRegCopy className="mr-2" />}
            >
              复制条码
            </Button>
            <Button
              loading={loading}
              disabled={loading || !code}
              onClick={download}
              icon={<BsArrowDownCircle className="mr-2" />}
            >
              下载条码
            </Button>
          </div>

          <div className="mt-6 py-6 border-t border-gray-300 dark:border-gray-700 text-center text-xs">
            <div className="opacity-60">
              <p>
                Waboba offers this application to create uniform barcode designs
                by generating barcodes in PDF format. To use it, simply enter a
                barcode number, select the preferred barcode type, and then
                click on the 'Generate' button. Each barcode produced colored in
                CMYK black (0,0,0,100), making it seamlessly integrable into
                packaging design files.
              </p>
              <p className="mt-2">
                Please note that Waboba has acquired the license to use this
                specific font. Consequently, these barcodes should be used
                solely for Waboba products.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodePage;
